/**
 * DeclarationSubmissionPort — self-authored, unagreed upstream
 * (ADR-20261015-bzyr, extending the ADR-20260826-ggnj pattern the other
 * three self-authored ports in this directory already follow). Contract
 * artefact: ./contracts/declaration-submission.contract.json.
 *
 * ADR-20261015-bzyr maps the product spec's three ports as: renewal data
 * = RenewalSummaryPort + ExposurePrefillPort; save = WorkingCopyPort;
 * submission = this port, new in this task group (TG-4). The live
 * submission endpoint is not called by this feature — US-SBC-004 and
 * US-SBC-009 (product-spec "Out of Scope") own that; this port and its
 * stub exist so `m1-declaration-next-steps` has a shaped seam to build
 * against, exactly as `ExposurePrefillPort` was shaped ahead of
 * `m1-exposure-read-endpoints-us-epic-005` owning the real read.
 *
 * Submission authority stays with a licensed human acting as the
 * renewal's owner (NFR-gag78j: a suggestion is never a recommendation;
 * bind and advice authority never move to this port). `capability` is
 * carried on every request precisely so the port itself enforces that: a
 * contributor's `canSubmitRenewal` is `false` under every delegation,
 * including one naming every section (`capability/capability.ts`'s own
 * doc comment), and `submit` refuses with 403 before it even looks at
 * section completion (AC-21, AC-32).
 *
 * Section completion is reported by the CALLER as
 * {@link SectionCompletionStatus}, not read by this port itself — this
 * port owns no renewal-wide section store. `"completed-by-colleague"` is
 * the exact fact `data.section_assignments`' real `state = 'completed'`
 * row records for a delegated section (TG-2, `src/api/functions/
 * contributor_sections.py`'s own doc comment: "a 'completed' row whose
 * assignee is a colleague, not the owner, is exactly that fact; no
 * separate status value exists or is needed for it"). This port's own
 * seam test (./stubs/__tests__/declaration-submission-seam.test.ts)
 * exercises that real backend state machine, not a mock of it, before
 * trusting this literal string as the completion marker.
 */
import type { OutcomeFailureDetail, WriteOutcome } from "./outcome";

export type SectionCompletionState = "completed" | "completed-by-colleague" | "not-completed";

export interface SectionCompletionStatus {
  sectionId: string;
  state: SectionCompletionState;
}

/** The one capability field this port reads (`capability/capability.ts`'s `Capability.canSubmitRenewal`).
 * Declared as its own narrow type, rather than importing `Capability` itself, so this port does not
 * pull the whole capability module — and the role-branch it is built not to need — into its own
 * dependency graph for the sake of one boolean. */
export interface SubmissionCapability {
  canSubmitRenewal: boolean;
}

export interface DeclarationSubmissionRequest {
  renewalId: string;
  capability: SubmissionCapability;
  /** One entry per section in the route table (routing/route-table.ts's `SECTION_TABLE`). An
   * inapplicable section (routing/section-state.ts's own "inapplicable" state) is reported
   * `"completed"` by the caller — it was never the customer's or a colleague's to complete, and
   * section-state.ts already treats "confirmed or inapplicable" as settled for exactly this reason. */
  sections: readonly SectionCompletionStatus[];
  /**
   * TG-4 (m1-declaration-next-steps): the two fields the REAL upstream (`POST
   * /api/portal/submission`, `src/api/functions/submission.py`) actually reads from the request
   * body — `representativeName` (trimmed, non-empty) and `agreed` (must be the literal `true` the
   * declaration screen's own checkbox guarantees before `onSubmit` ever fires). Optional on this
   * shared port interface, not required, so the pre-existing stub and its own test suite
   * (`stubs/declaration-submission.stub.ts` and its tests, which construct this request with
   * neither field and assert on the stub's own capability/section-driven refusals only) keep
   * compiling and passing unchanged — the stub never reads either field. `createHttpDeclarationSubmissionPort`
   * (./http/declaration-submission-client.ts) is the one implementation that requires both to be
   * present to do anything useful; see that module's own doc comment.
   */
  representativeName?: string | undefined;
  agreed?: boolean | undefined;
}

export interface DeclarationSubmissionResult {
  renewalId: string;
  submittedAt: string;
}

/** One 422 `invalid_declaration` field refusal, mirroring `src/api/functions/submission.py`'s own
 * `_validate_declaration_body` return shape (`{field, reason}`) byte for byte. `field` is widened to
 * a plain `string` (not a closed union of the two names the server happens to validate today) so a
 * caller that only ever maps the two fields this feature's form actually has
 * (`App.tsx`'s `fieldErrorsToSummaryItems`) does not need updating the moment the server adds a
 * third validated field it has no opinion on. */
export interface DeclarationFieldError {
  field: string;
  reason: string;
}

/**
 * `DeclarationSubmissionPort["submit"]`'s Failed/Indeterminate `detail` is still, structurally, an
 * {@link OutcomeFailureDetail} (the shared shape every port in this directory's `WriteOutcome`
 * carries) — this is an additive extension, not a replacement: every field `OutcomeFailureDetail`
 * already declares is present and means exactly what it always has. The two extra, OPTIONAL fields
 * below exist because this is the one port in the directory whose 422 refusal is not a single flat
 * sentence (ADR-20261022-yyjf's `invalid_declaration` names a field error PER field; `incomplete_sections`
 * names a section id per section) — a caller that does not know about this interface still reads
 * `.httpStatus`/`.taxonomyCode`/`.message` exactly as it always could. `taxonomyCode` carries one of
 * the closed codes below; callers switch on it, never on `httpStatus` alone, since 422 is shared by
 * two distinct refusals with two distinct UI treatments (AC: "422 invalid_declaration shows the
 * field errors ...; 422 incomplete_sections returns the owner to the section list").
 */
export interface DeclarationSubmissionFailureDetail extends OutcomeFailureDetail {
  /** Present only when `taxonomyCode === "invalid_declaration"`. */
  fieldErrors?: readonly DeclarationFieldError[] | undefined;
  /** Present only when `taxonomyCode === "incomplete_sections"` (the server-side completeness
   * backstop, ADR-20261022-yyjf — main and per-policy sections only; DEBT-20261022-p27e tracks the
   * narrower scope). */
  incompleteSectionIds?: readonly string[] | undefined;
}

/** The closed set of `taxonomyCode` values `createHttpDeclarationSubmissionPort` ever sets on a
 * Failed/Indeterminate outcome's `detail.taxonomyCode` — named once here so `App.tsx` and the HTTP
 * client agree on the exact strings without either hardcoding the other's literal. The stub
 * (`stubs/declaration-submission.stub.ts`) sets none of these on ITS OWN 403/422 outcomes (that
 * file is unowned by this task group and its own test suite asserts an exact `toEqual` match with
 * no `taxonomyCode` key at all) — a caller that switches on this field must still have a sane
 * fallback for "no code at all", see `App.tsx`'s own `classifySubmissionFailure`. */
export const DECLARATION_SUBMISSION_FAILURE_CODES = {
  roleNotPermitted: "role_not_permitted_to_submit",
  invalidDeclaration: "invalid_declaration",
  incompleteSections: "incomplete_sections",
  renewalSubmitted: "renewal_submitted",
  submissionUnavailable: "submission_unavailable",
} as const;

export interface DeclarationSubmissionPort {
  /**
   * Submits the renewal's declaration. Refuses with httpStatus 403 when `capability.canSubmitRenewal`
   * is `false` (AC-21, AC-32) — checked before section completion, never after. Refuses with
   * httpStatus 422 naming every section still `"not-completed"` when `sections` carries one. The
   * stub never returns an Indeterminate outcome (ADR-20260821-kp45's third member exists for a write
   * whose result could not be confirmed after it was sent — a stub that never actually sends
   * anything has no such case to report); `createHttpDeclarationSubmissionPort` does, for a dropped
   * network connection (the same rule every other HTTP-backed write port in this directory follows).
   */
  submit(request: DeclarationSubmissionRequest): Promise<WriteOutcome<DeclarationSubmissionResult>>;
}
